import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { NotificationEmptyComponent, SummaryCardAction, SummaryCardComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointServiceInjector } from '@next/core-lib/responsive';

import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger } from '@angular/material/menu';
import { EmptyWidgetComponent } from '../../../shared/components/empty-widget.component';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner.component/loading-spinner.component';
import { DiseaseHeaderComponent } from '../../../shared/components/widget-components/header.component';
import { DiseaseDashboardWidgetTableComponent } from '../../../shared/components/widget-components/table.component';
import { DateService } from '../../../shared/services/date.service';
import { CropYearService } from '../../../shared/services/crop-year.service';
import { areDateRangesSameDay, getDefaultDiseaseWidgetSelectedDates } from '../../../shared/utils/date-range.utils';
import { BlightCropFieldAdvice } from '../../models/models';
import { ActionMenuComponent } from '../../shared/components/blight-dashboard-action-menu.component';
import { defaultBlightDayRange } from '../../shared/helpers/blight-helpers';
import { BlightAIIdentifiers, eLearningUrl } from '../../shared/constants';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';
import { RecalculateButtonComponent } from '@app/shared/components/recalculate-button/recalculate-button.component';
import { BlightSignalStore } from '../../signal-store/blight-signal-store';

@Component({
  selector: 'app-blight-dashboard-widget',
  templateUrl: './blight-dashboard-widget.component.html',
  styleUrls: ['./blight-dashboard-widget.component.scss'],
  imports: [
    SummaryCardComponent,
    DiseaseHeaderComponent,
    MatDivider,
    MatButton,
    MatMenuTrigger,
    ActionMenuComponent,
    MatIcon,
    LoadingSpinnerComponent,
    DiseaseDashboardWidgetTableComponent,
    NotificationEmptyComponent,
    EmptyWidgetComponent,
    TranslatePipe,
    RecalculateButtonComponent,
  ],
})
export class BlightDashboardWidgetComponent {
  private readonly localeService = inject(LocaleService);
  private readonly router = inject(Router);
  private readonly breakpointService = inject(BreakpointServiceInjector);
  private readonly ai = inject(AppInsightsServiceInjector);
  private readonly dateService = inject(DateService);
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(BlightSignalStore);
  protected actions = [
    {
      id: 'info',
      icon: 'info_outline',
      callback: () => window.open(eLearningUrl),
    },
  ] satisfies SummaryCardAction[];

  protected readonly tgaCropfieldId = input<number | null>(null);
  protected readonly isTga = input<boolean>(false);
  protected readonly selectedCropFieldAdvices = signal<BlightCropFieldAdvice[]>([]);

  protected readonly advicesToShow = computed(() => {
    if (!this.isTga()) {
      return this.store.visibleBlightAdvices();
    }
    const id = this.tgaCropfieldId();
    const visibleAdvices = this.store.visibleBlightAdvices();
    const filtered = visibleAdvices.filter((a) => id !== null && id === a.cropFieldId);
    return filtered;
  });

  protected readonly selectedDates = signal<Date[]>(
    getDefaultDiseaseWidgetSelectedDates(this.dateService, this.store.cropYear()),
  );
  protected readonly maximalDate = computed(() =>
    this.dateService.daysInTheFuture(defaultBlightDayRange.daysInFuture, this.store.cropYear()),
  );
  protected readonly isLoading = computed(
    () => this.store.blightDataIsLoading() || this.store.participatingCropFieldsIsLoading(),
  );
  protected readonly hasBlightAdvices = computed(() => this.store.visibleBlightAdvices().length > 0);
  private readonly ltMd = toSignal(this.breakpointService.ltMd$, { initialValue: false });
  protected readonly showAdviceButton = computed(() => !this.ltMd());
  private readonly amountOfSelectedCropFields = computed(() => this.selectedCropFieldAdvices().length);
  protected readonly isActionButtonDisabled = computed(() => this.amountOfSelectedCropFields() === 0);
  protected readonly actionButtonLabel = computed(() => {
    const count = this.amountOfSelectedCropFields();
    return count > 0
      ? this.localeService.translate('blightV2.dashboardWidget.addAction', { count: count.toString() })
      : this.localeService.translate('blightV2.dashboardWidget.addActionDisabled');
  });
  protected readonly isRecalculationDisabled = computed(
    () =>
      this.store.isBlightCropFieldsSyncing() || !this.hasBlightAdvices() || !this.store.hasEligibleBlightCropFields(),
  );
  protected readonly recalculationLabelKey = computed(() =>
    this.store.isBlightCropFieldsSyncing() ? 'blightV2.shared.recalculating' : 'blightV2.shared.recalculateAdviceAll',
  );

  protected async onViewAdvice(): Promise<void> {
    this.ai.trackEvent(BlightAIIdentifiers.viewAdvice, {});
    await this.router.navigate(['/blightV2']);
  }

  protected onSelectedDatesChange(dates: Date[]): void {
    if (dates.length < 2 || areDateRangesSameDay(this.dateService, dates, this.selectedDates())) {
      return;
    }

    this.store.patchSelectedDates(dates);
    this.selectedDates.set(dates);
  }

  protected onRecalculate(): void {
    this.ai.trackEvent(BlightAIIdentifiers.recalc, {});
    this.store.requestBlightRecalculation(this.store.eligibleBlightRecalculationRequests());
  }

  protected onCropFieldSelectionChange(advices: CropFieldAdvice[]): void {
    this.selectedCropFieldAdvices.set(advices as BlightCropFieldAdvice[]);
  }

  protected onAction(): void {
    this.ai.trackEvent(BlightAIIdentifiers.addActionMyArable, {});
  }
}
